/**
 * Dashboard Campaign Section Component
 *
 * Purpose: Display user's campaigns with real data from API
 * WHO: Dashboard users viewing their campaigns
 * WHAT: List campaigns with pagination, loading, empty states
 * WHERE: Dashboard page - main content area
 * WHY: Show users their active campaigns and let them navigate to them
 *
 * INTEGRATION POINT: Uses CampaignContext.loadCampaigns()
 * Contract: shared/contracts/campaign/v1/campaign-list.yaml
 *
 * DECISION: Option A - Numbered pagination (5 per page)
 * DECISION: Option C - Activity feed commented out (feature flag)
 *
 * Updated 2026-02-23: Section 0 - Dashboard empty state & error state distinction
 *   - Empty state: scroll-themed "No Campaigns Yet" card with "Create My First Campaign" CTA
 *   - Error state: visually distinct red-bordered card with Retry button
 *   - "+ New Campaign" button always visible in header regardless of state
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Button,
  Heading,
  Text,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Spinner,
  Center,
  useToast,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Icon,
  Badge,
} from "@chakra-ui/react";
import { Plus, AlertCircle, ScrollText } from "lucide-react";
import { useNavigate } from "react-router";
import { useCampaignContext } from "../../context/CampaignContext";
import { CampaignCard } from "../campaign/CampaignCard";
import { theme as mythboundTheme } from "../../config/theme";

const CAMPAIGNS_PER_PAGE = 5;

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * Custom Pagination Component (Option A - Numbered pagination)
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <HStack justify="center" spacing={2} mt={8}>
      {/* Previous button */}
      <Button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        isDisabled={currentPage === 1}
        variant="outline"
        size="sm"
      >
        Previous
      </Button>

      {/* Page buttons */}
      {pages.map((page) => (
        <Button
          key={page}
          onClick={() => onPageChange(page)}
          variant={currentPage === page ? "solid" : "outline"}
          colorScheme={currentPage === page ? "blue" : undefined}
          size="sm"
          minW="40px"
        >
          {page}
        </Button>
      ))}

      {/* Next button */}
      <Button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        isDisabled={currentPage === totalPages}
        variant="outline"
        size="sm"
      >
        Next
      </Button>

      {/* Page info */}
      <Text fontSize="sm" color="gray.600" ml={4}>
        Page {currentPage} of {totalPages}
      </Text>
    </HStack>
  );
};

/**
 * Skeleton loader for campaign cards
 */
const CampaignSkeletons: React.FC<{ count: number }> = ({ count }) => {
  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardBody>
            <VStack spacing={3} align="start">
              <Skeleton height="32px" width="200px" />
              <SkeletonText noOfLines={2} width="100%" />
              <Skeleton height="20px" width="150px" />
            </VStack>
          </CardBody>
        </Card>
      ))}
    </SimpleGrid>
  );
};

/**
 * Empty state when user has no campaigns (API returned 200 + empty array)
 * Updated 2026-02-23: Scroll-themed card with "Create My First Campaign" CTA
 * Visually distinct from error state -- uses parchment/gold theming, not red
 */
const EmptyCampaignState: React.FC<{ onCreate: () => void }> = ({
  onCreate,
}) => {
  return (
    <Center py={12}>
      <Card
        w="full"
        maxW="460px"
        textAlign="center"
        bg="rgba(232, 217, 176, 0.08)"
        borderWidth="1px"
        borderColor={mythboundTheme.colors.primary.gold}
        boxShadow={mythboundTheme.shadows.goldGlow}
      >
        <CardBody>
          <VStack spacing={5}>
            {/* Scroll icon to reinforce the "scroll" / parchment theme */}
            <Icon
              as={ScrollText}
              w={16}
              h={16}
              color={mythboundTheme.colors.primary.gold}
            />
            <VStack spacing={2}>
              <Heading
                size="md"
                fontFamily={mythboundTheme.typography.fontFamily.heading}
                color={mythboundTheme.colors.secondary.white}
              >
                No Campaigns Yet
              </Heading>
              <Text
                color={mythboundTheme.colors.accent.silver}
                fontFamily={mythboundTheme.typography.fontFamily.body}
              >
                Your adventure awaits! Create your first campaign to begin
                your Mythbound™ journey.
              </Text>
            </VStack>
            <Button
              leftIcon={<Plus size={16} />}
              colorScheme="blue"
              onClick={onCreate}
              mt={2}
              size="lg"
              fontFamily={mythboundTheme.typography.fontFamily.heading}
            >
              Create My First Campaign
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </Center>
  );
};

/**
 * Error state when campaigns fail to load (API returned non-200 or network error)
 * Updated 2026-02-23: Red-themed card visually distinct from empty state
 * Updated 2026-02-24: Auth-aware error detection — shows "Sign in" prompt for
 * UNAUTHORIZED errors instead of generic "Failed to Load" message.
 */
const ErrorState: React.FC<{ error: string; onRetry: () => void }> = ({ error, onRetry }) => {
  const navigate = useNavigate();
  const isAuthError = error === 'UNAUTHORIZED';

  return (
    <Card
      borderColor={isAuthError ? mythboundTheme.colors.primary.gold : mythboundTheme.colors.functional.error}
      borderWidth="2px"
      bg={isAuthError ? "rgba(232, 217, 176, 0.06)" : "rgba(255, 82, 82, 0.06)"}
    >
      <CardBody>
        <HStack spacing={4}>
          <Icon
            as={isAuthError ? ScrollText : AlertCircle}
            w={8}
            h={8}
            color={isAuthError ? mythboundTheme.colors.primary.gold : mythboundTheme.colors.functional.error}
          />
          <VStack align="start" spacing={1} flex={1}>
            <Text
              fontWeight="bold"
              color={mythboundTheme.colors.secondary.white}
              fontFamily={mythboundTheme.typography.fontFamily.heading}
            >
              {isAuthError ? 'Sign In Required' : 'Failed to Load Campaigns'}
            </Text>
            <Text fontSize="sm" color={mythboundTheme.colors.accent.silver}>
              {isAuthError
                ? 'Sign in to view your campaigns and start your Mythbound™ adventure.'
                : 'Something went wrong while fetching your campaigns. Please check your connection and try again.'}
            </Text>
          </VStack>
          <Button
            size="sm"
            colorScheme={isAuthError ? "blue" : "red"}
            variant="solid"
            onClick={isAuthError ? () => navigate('/login') : onRetry}
            fontFamily={mythboundTheme.typography.fontFamily.heading}
          >
            {isAuthError ? 'Sign In' : 'Retry'}
          </Button>
        </HStack>
      </CardBody>
    </Card>
  );
};

/**
 * Main Dashboard Campaign Section Component
 */
export const DashboardCampaignSection: React.FC = () => {
  const { campaigns, isLoading, error, loadCampaigns } = useCampaignContext();
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const toast = useToast();

  /**
   * Load campaigns on mount and when pagination changes
   * INTEGRATION POINT: Calls CampaignContext.loadCampaigns()
   */
  useEffect(() => {
    loadCampaigns();
  }, [currentPage, loadCampaigns]);

  // Calculate pagination values
  const totalPages = Math.ceil((campaigns.length || 0) / CAMPAIGNS_PER_PAGE);
  const startIndex = (currentPage - 1) * CAMPAIGNS_PER_PAGE;
  const endIndex = startIndex + CAMPAIGNS_PER_PAGE;
  const paginatedCampaigns = campaigns?.slice(startIndex, endIndex) || [];

  // ---------------------------------------------------------------------------
  // SHARED HEADER: "+ New Campaign" button always visible regardless of state
  // Updated 2026-02-23: Extracted header so it renders in loading, error,
  // empty AND populated states for consistent navigation
  // ---------------------------------------------------------------------------
  const sectionHeader = (
    <HStack justify="space-between" w="full">
      <Heading
        size="lg"
        fontFamily={mythboundTheme.typography.fontFamily.heading}
        color={mythboundTheme.colors.secondary.white}
      >
        Your Campaigns
      </Heading>
      <Button
        leftIcon={<Plus size={16} />}
        colorScheme="blue"
        onClick={() => navigate("/campaign/builder")} // Fixed 2026-02-26: correct route is /campaign/builder
      >
        {/* Fixed 2026-02-26: icon provides the +, removed duplicate + from label */}
        New Campaign
      </Button>
    </HStack>
  );

  // Loading state: Show skeleton loaders (Option A decision)
  if (isLoading) {
    return (
      <Box>
        <VStack align="start" spacing={6}>
          {sectionHeader}
          <CampaignSkeletons count={3} />
        </VStack>
      </Box>
    );
  }

  // Error state -- visually distinct from empty state (red border + bg)
  if (error) {
    return (
      <Box>
        <VStack align="start" spacing={4}>
          {sectionHeader}
          <ErrorState error={error} onRetry={loadCampaigns} />
        </VStack>
      </Box>
    );
  }

  // Empty state -- API returned 200 with empty campaigns array (scroll-themed)
  if (!campaigns || campaigns.length === 0) {
    return (
      <Box>
        <VStack align="start" spacing={6} w="full">
          {sectionHeader}
          <EmptyCampaignState
            onCreate={() => navigate("/campaign/builder")} // Fixed 2026-02-26: correct route is /campaign/builder
          />
        </VStack>
      </Box>
    );
  }

  // Main content: Campaign cards + pagination (Option A - numbered pagination)
  return (
    <VStack align="start" spacing={8} w="full">
      {/* Header with title and CTA -- consistent with loading/error/empty headers */}
      <HStack justify="space-between" w="full">
        <VStack align="start" spacing={1}>
          <Heading
            size="lg"
            fontFamily={mythboundTheme.typography.fontFamily.heading}
            color={mythboundTheme.colors.secondary.white}
          >
            Your Campaigns
          </Heading>
          <Text color={mythboundTheme.colors.accent.silver} fontSize="sm">
            {campaigns.length} total {campaigns.length === 1 ? "campaign" : "campaigns"}
          </Text>
        </VStack>
        <Button
          leftIcon={<Plus size={16} />}
          colorScheme="blue"
          onClick={() => navigate("/campaign/builder")} // Fixed 2026-02-26: correct route is /campaign/builder
        >
          {/* Fixed 2026-02-26: icon provides the +, removed duplicate + from label */}
          New Campaign
        </Button>
      </HStack>

      {/* Campaign grid */}
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3 }}
        spacing={4}
        w="full"
      >
        {paginatedCampaigns.map((campaign) => (
          <CampaignCard
            key={campaign.campaign_id}
            campaign={campaign}
            onSelect={() => navigate(`/campaigns/${campaign.campaign_id}`)}
          />
        ))}
      </SimpleGrid>

      {/* Pagination (only show if > 1 page) */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Info text showing current view */}
      {totalPages > 1 && (
        <Text fontSize="xs" color="gray.500">
          Showing {startIndex + 1}-{Math.min(endIndex, campaigns.length)} of{" "}
          {campaigns.length} campaigns
        </Text>
      )}
    </VStack>
  );
};

export default DashboardCampaignSection;

/**
 * FEATURE FLAG: Activity Feed (commented out per Option C decision)
 *
 * TODO: Re-enable when activity tracking implemented
 * Set REACT_APP_ENABLE_ACTIVITY_FEED=true in .env to re-enable
 *
 * Current implementation uses CampaignContext which doesn't track activities yet.
 * When implemented, hook into ActivityService and add to dashboard:
 *
 * const shouldShowActivity = process.env.REACT_APP_ENABLE_ACTIVITY_FEED === 'true';
 *
 * {shouldShowActivity && (
 *   <Box mt={8}>
 *     <Divider mb={6} />
 *     <Heading size="md" mb={4}>Recent Activity</Heading>
 *     <ActivityFeed activities={activities} isLoading={activityLoading} />
 *   </Box>
 * )}
 */
